import type { JsonSchemaNode } from "@nmblr-platform-flow/schema";
import { useRef } from "react";
import { useTemplateAutocomplete } from "../../../hooks/useTemplateAutocomplete.js";
import { humanize } from "../../../lib/humanize.js";
import type { TemplateVariable } from "../../../lib/schema-paths.js";
import { TemplateDropdown } from "./TemplateDropdown.js";

interface Props {
	name: string;
	schema: JsonSchemaNode;
	value: unknown;
	onChange: (value: unknown) => void;
	required?: boolean;
	templateVariables?: TemplateVariable[];
}

export function MultilineInput({
	name,
	schema,
	value,
	onChange,
	required,
	templateVariables,
}: Props) {
	const id = `field-${name}`;
	const placeholder = schema["x-forge-placeholder"] ?? undefined;
	const strValue =
		typeof value === "string" ? value : ((schema.default as string | undefined) ?? "");
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const isTemplateEnabled =
		schema["x-forge-template"] === true && templateVariables && templateVariables.length > 0;
	const autocomplete = useTemplateAutocomplete(isTemplateEnabled ? templateVariables : undefined);

	const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		const newValue = e.target.value;
		onChange(newValue);
		if (isTemplateEnabled && textareaRef.current) {
			autocomplete.handleInputChange(
				newValue,
				e.target.selectionStart ?? newValue.length,
				textareaRef.current,
			);
		}
	};

	const handleSelect = (path: string) => {
		if (!textareaRef.current) return;
		const cursorPos = textareaRef.current.selectionStart ?? strValue.length;
		const { newValue, newCursorPos } = autocomplete.handleSelect(path, strValue, cursorPos);
		onChange(newValue);
		// Restore cursor position after React re-render
		requestAnimationFrame(() => {
			textareaRef.current?.setSelectionRange(newCursorPos, newCursorPos);
			textareaRef.current?.focus();
		});
	};

	return (
		<label className="mb-3 block" htmlFor={id}>
			<span className="text-xs text-text-muted">
				{humanize(name)}
				{required && <span className="ml-1 text-error">*</span>}
			</span>
			<textarea
				ref={textareaRef}
				id={id}
				value={strValue}
				onChange={handleChange}
				placeholder={placeholder}
				rows={5}
				className="mt-1 block w-full rounded-xl border border-border bg-canvas px-3 py-2 font-mono text-xs text-text-primary placeholder:text-text-muted/50 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
			/>
			{autocomplete.state.isOpen && isTemplateEnabled && (
				<TemplateDropdown
					variables={templateVariables ?? []}
					filter={autocomplete.state.filter}
					onSelect={handleSelect}
					onDismiss={autocomplete.dismiss}
					position={autocomplete.state.position}
				/>
			)}
			{schema.description && (
				<span className="mt-1 block text-xs text-text-muted">{schema.description}</span>
			)}
		</label>
	);
}
